/* Phase 2 primitive: the one HMS button.
 *
 * Every action button in the app is one of five shapes that screens keep
 * re-declaring in their own CSS. This collects them so a screen writes
 * `<HMSButton variant="primary">` instead of another `.xyz-save-btn`
 * block.
 *
 * Props:
 *  - variant:  'primary' | 'secondary' | 'saveDraft' | 'danger'
 *              | 'success' | 'ghost' | 'dangerGhost' | 'icon'
 *              (default 'primary')
 *  - size:     'compact' shrinks to the header-row size; omit for the
 *              full-size form/footer button
 *  - disabled: standard <button> disabled (hover motion is suppressed)
 *  - type:     'button' by default so a button inside a <form> never
 *              submits it by accident
 *  - className / children / any other <button> prop is passed through
 *
 * The `icon` variant is transparent and tints on hover with 10% of its
 * OWN colour, so the caller picks the tone the normal way — `color` via
 * a style prop or a class — and the hover fill follows it.
 *
 * `ghost` and `dangerGhost` are the soft-tint row actions the Template
 * HQ migration surfaced — four separate local classes there were all
 * re-cutting the same recipe. They sit in the danger shape family
 * (6px radius, the tighter padding) and deepen their own tint on hover
 * rather than lifting, because they appear inside dense rows where a
 * translate would shove the row around.
 */
import React from 'react';
import './HMSButton.css';

// Prop values stay camelCase (JS side), class names stay kebab (CSS side).
const VARIANT_CLASS = {
  primary: 'hms-btn-primary',
  secondary: 'hms-btn-secondary',
  saveDraft: 'hms-btn-save-draft',
  danger: 'hms-btn-danger',
  success: 'hms-btn-success',
  ghost: 'hms-btn-ghost',
  dangerGhost: 'hms-btn-danger-ghost',
  icon: 'hms-btn-icon',
};

const HMSButton = ({
  variant = 'primary',
  size,
  type = 'button',
  className = '',
  children,
  ...rest
}) => {
  const cls = [
    'hms-btn',
    VARIANT_CLASS[variant] || VARIANT_CLASS.primary,
    size === 'compact' ? 'hms-btn-compact' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <button type={type} className={cls} {...rest}>
      {children}
    </button>
  );
};

export default HMSButton;
